import React, { useEffect, useState } from 'react'
import axios from 'axios'
import styled from 'styled-components'
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, Button } from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { translate } from 'utils/intl/helper'
import { useIntl } from 'react-intl'
import { KEYS } from 'utils/intl/constant'
import constant from '../../../utils/constant'

const EnergyLabelThumbnailContainer = styled(Box)`
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 2px;
    width: 60px;
    max-width: 60px;
    cursor: pointer;
    background-color: #fafafa;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 100%;
        height: auto;
        display: block;
    }
`

const DialogTitleContainer = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #e0e0e0;
`

interface EnergyLabelPreviewProps {
    selectedData: { id: string }
}

const EnergyLabelPreview: React.FC<EnergyLabelPreviewProps> = ({ selectedData }) => {
    const intl = useIntl()
    const [energyLabelSvg, setEnergyLabelSvg] = useState<string | null>(null)
    const [isEnergyLabelLoading, setIsEnergyLabelLoading] = useState(false)
    const [isDialogOpen, setIsDialogOpen] = useState(false)

    useEffect(() => {
        if (!selectedData || !selectedData.id) {
            setEnergyLabelSvg(null)
            setIsEnergyLabelLoading(false)
            return
        }

        let isCancelled = false

        const fetchEnergyLabel = async () => {
            try {
                setIsEnergyLabelLoading(true)
                setEnergyLabelSvg(null)

                const url = `${constant.backend.GENERATE_ENERGY_LABEL}/${selectedData.id}`

                const response = await axios.get(url, {
                    responseType: 'text',
                })

                if (!isCancelled) {
                    setEnergyLabelSvg(response.data)
                }
            } catch (err) {
                if (!isCancelled) {
                    console.error(err)
                    setEnergyLabelSvg(null)
                }
            } finally {
                if (!isCancelled) {
                    setIsEnergyLabelLoading(false)
                }
            }
        }

        fetchEnergyLabel()

        return () => {
            isCancelled = true
        }
    }, [selectedData])

    const handleDownloadEnergyLabel = () => {
        if (!energyLabelSvg) return

        const element = document.createElement('a')
        const file = new Blob([energyLabelSvg], { type: 'image/svg+xml' })
        element.href = URL.createObjectURL(file)
        element.download = `energy-label-${selectedData?.id || 'export'}.svg`
        document.body.appendChild(element)
        element.click()
        document.body.removeChild(element)
        URL.revokeObjectURL(element.href)
    }

    if (!selectedData) return null

    return (
        <>
            {isEnergyLabelLoading && !energyLabelSvg && (
                <Box display='flex' alignItems='center' justifyContent='center'>
                    <CircularProgress size={16} />
                </Box>
            )}

            {energyLabelSvg && (
                <EnergyLabelThumbnailContainer onClick={() => setIsDialogOpen(true)}>
                    <div dangerouslySetInnerHTML={{ __html: energyLabelSvg }} />
                </EnergyLabelThumbnailContainer>
            )}

            <Dialog
                open={isDialogOpen && !!energyLabelSvg}
                onClose={() => setIsDialogOpen(false)}
                maxWidth='md'
                fullWidth>
                <DialogTitleContainer>
                    <DialogTitle sx={{ padding: 0 }}>{translate(intl, KEYS.UI_MODEL_INFORMATION)}</DialogTitle>
                    <Button
                        variant='contained'
                        startIcon={<FileDownloadIcon />}
                        onClick={handleDownloadEnergyLabel}
                        size='small'>
                        {translate(intl, KEYS.UI_DOWNLOAD)}
                    </Button>
                </DialogTitleContainer>
                <DialogContent dividers>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            overflow: 'auto',
                            '& svg': {
                                maxWidth: '100%',
                                height: 'auto',
                                display: 'block',
                            },
                        }}>
                        {energyLabelSvg && <div dangerouslySetInnerHTML={{ __html: energyLabelSvg }} />}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default EnergyLabelPreview
